import { useNavigate } from "react-router-dom";
import { PageSEO } from "@/components/seo/PageSEO";
import { LandingHeader } from "@/components/layout/LandingHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { 
  Mic, 
  Sparkles, 
  ArrowRight, 
  CheckCircle, 
  MessageSquare,
  Brain,
  Zap,
  Target,
  Users,
  Clock,
  TrendingUp,
  ClipboardList,
  Lightbulb,
  BarChart3
} from "lucide-react";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { FeaturePageFooter } from "@/components/features/FeaturePageFooter";

export default function VoiceInterviewInfo() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Mic,
      title: "Passive Recording",
      description: "AI quietly records and transcribes your entire conversation with clients without interrupting the natural flow of dialogue."
    },
    {
      icon: Brain,
      title: "Real-Time Transcription",
      description: "Every word from both auditor and client is captured and transcribed instantly, creating a complete record of the conversation."
    },
    {
      icon: Zap,
      title: "Post-Interview Analysis",
      description: "After the interview ends, AI analyzes the full transcript to identify opportunities, pain points, and digital transformation potential."
    },
    {
      icon: Target,
      title: "Full Context Understanding",
      description: "AI reviews the entire conversation as a whole, detecting patterns and connections across the complete dialogue."
    },
    {
      icon: Clock,
      title: "Hands-Free Documentation",
      description: "Focus completely on your client. No typing, no note-taking—just natural conversation while AI documents everything."
    },
    {
      icon: TrendingUp,
      title: "Comprehensive Opportunity Extraction",
      description: "Get detailed opportunity reports after each interview, with automatic categorization and prioritization ready for review."
    }
  ];

  const benefits = [
    "Give your full attention to clients without distraction",
    "Never miss important details while maintaining eye contact",
    "Capture complete conversations including subtle nuances",
    "Eliminate manual note-taking and post-interview documentation",
    "Ensure consistent opportunity capture across all interviews",
    "Get objective AI analysis of every client conversation"
  ];

  const useCases = [
    {
      title: "Client Discovery Interviews",
      description: "Record auditor-client conversations to capture pain points, challenges, and improvement opportunities for later analysis."
    },
    {
      title: "Stakeholder Conversations",
      description: "Document discussions with multiple stakeholders, letting AI identify common themes and conflicting requirements."
    },
    {
      title: "Process Documentation Sessions",
      description: "Record clients explaining their workflows in detail, with AI extracting inefficiencies and automation candidates."
    },
    {
      title: "Requirement Validation Meetings",
      description: "Capture client feedback and requirements discussions, ensuring nothing gets lost in translation."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="OpportunityIQ | AI Voice Interview Recording | Auditic"
        description="Record client interviews with real-time AI transcription and automatic opportunity extraction. Focus on the conversation while AI handles the notes."
        canonicalPath="/voice-interview-info"
      />
      <LandingHeader />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="text-center max-w-4xl mx-auto mb-16">
              <Badge variant="secondary" className="mb-4 bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30">
                <Sparkles className="w-4 h-4 mr-2" />
                BREAKTHROUGH FEATURE
              </Badge>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-green-400 to-yellow-400 bg-clip-text text-transparent">
                OpportunityIQ
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8">
                Record your client interviews and let AI automatically transcribe conversations and extract opportunities
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="default" onClick={() => navigate("/try-analysis")}>
                  Start a Free Trial today
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/pricing")}>
                  View Pricing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </ScrollReveal>

          {/* How It Works */}
          <ScrollReveal>
            <Card className="max-w-5xl mx-auto p-8 mb-16 bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold">How It Works</h2>
              </div>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3 text-lg font-bold text-purple-300">
                    1
                  </div>
                  <h3 className="font-semibold mb-2">Start Recording</h3>
                  <p className="text-sm text-muted-foreground">Begin recording before your client interview</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3 text-lg font-bold text-purple-300">
                    2
                  </div>
                  <h3 className="font-semibold mb-2">Conduct Interview</h3>
                  <p className="text-sm text-muted-foreground">Have a natural conversation with your client</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3 text-lg font-bold text-purple-300">
                    3
                  </div>
                  <h3 className="font-semibold mb-2">AI Transcribes</h3>
                  <p className="text-sm text-muted-foreground">Real-time transcription of the entire conversation</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3 text-lg font-bold text-purple-300">
                    4
                  </div>
                  <h3 className="font-semibold mb-2">Extract Opportunities</h3>
                  <p className="text-sm text-muted-foreground">AI analyzes full transcript for insights</p>
                </div>
              </div>
            </Card>
          </ScrollReveal>

          {/* Key Features */}
          <ScrollReveal>
            <div className="max-w-6xl mx-auto mb-16">
              <h2 className="text-4xl font-bold text-center mb-12">Powerful Capabilities</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-purple-400" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </Card>
                  );
                })}
              </div>
            </div>
          </ScrollReveal>

          {/* Discovery Questions Feature */}
          <ScrollReveal>
            <Card className="max-w-5xl mx-auto p-8 mb-16 bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold">Built-In Discovery Questions</h2>
                  <p className="text-muted-foreground text-sm">Industry-tailored checklists to guide every interview</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="font-semibold">Industry-Aware</h3>
                  <p className="text-sm text-muted-foreground">
                    Questions automatically adapt to over 30 industries—manufacturing, healthcare, finance, and more—so you always ask what matters most.
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="font-semibold">Categorised Checklists</h3>
                  <p className="text-sm text-muted-foreground">
                    Questions are grouped by Pain Points, ROI Signals, Decision Process, and more—aligned with Deal Confidence scoring factors.
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="font-semibold">Track Your Progress</h3>
                  <p className="text-sm text-muted-foreground">
                    A live progress bar shows how many questions you've covered, ensuring thorough discovery before the interview ends.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-border/50">
                <div className="flex flex-wrap gap-2">
                  {["Pain Points", "ROI & Budget", "Decision Process", "Technology & Systems", "Timeline & Urgency"].map((cat) => (
                    <span key={cat} className="text-xs px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          </ScrollReveal>

          {/* Use Cases */}
          <ScrollReveal>
            <div className="max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl font-bold text-center mb-12">Perfect For</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {useCases.map((useCase, index) => (
                  <Card key={index} className="p-6 bg-muted/30">
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-400" />
                      {useCase.title}
                    </h3>
                    <p className="text-muted-foreground">{useCase.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* Benefits List */}
          <ScrollReveal>
            <Card className="max-w-4xl mx-auto p-8 mb-16 bg-gradient-to-br from-background to-muted/30">
              <h2 className="text-3xl font-bold text-center mb-8">Why Consultants Love It</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </Card>
          </ScrollReveal>


          {/* CTA Section */}
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Interview Process?</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Experience AI-powered interview recording and opportunity extraction
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="default" onClick={() => navigate("/try-analysis")}>
                  Start a Free Trial today
                  <Sparkles className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/pricing")}>
                  Compare Plans
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      
      <FeaturePageFooter />
    </div>
  );
}
